/**
 * Options description:
 * https://github.com/reactivestack/cookies/tree/master/packages/universal-cookie#readme
 */
import Cookies, { CookieChangeListener, CookieGetOptions, CookieSetOptions } from 'universal-cookie';

/**
 * Class @CookieStorageService for managing reading, writing, removing, clearing and listen for change in
 * document.cookie.
 * @CookieStorageService is wrapper over Cookies class from library universal-cookie (universal-cookie is used under
 * the hood of react-cookie and since we don't need HOCs and hooks we switched to smaller one lib universal-cookie).
 * document.cookie is primitive string for managing and forces using strings or to write our own similar class
 * like Cookies, and we decide to use Cookies from universal-cookie since is small library.
 */
class CookieStorageService {
  cookieStorage: Cookies;

  constructor() {
    this.cookieStorage = new Cookies();
  }

  /**
   * Set item to cookie.
   * Avoid to set Cookie values with null/undefined because cookie stores all values into document.cookie string.
   * After setting cookie values to document.cookie, every key and value becomes part of document.cookie string.
   * And read could be very confusing if you earlier send null or undefined for values.
   * Because of possible wrong interpretation of read value: like null of undefined could be read as string values.
   * Which could case problems and bugs in application. If for some reason you are forced to store null or undefined
   * values, map that logic into some reserved string constants, but if you insist on storing null/undefined get/getAll
   * could be proper handled by using doNotParse option.
   */
  set = (key: string, value: any, options?: CookieSetOptions): void => this.cookieStorage.set(key, value, options);

  /**
   * Get item value from cookie.
   */
  get = (key: string, options?: CookieGetOptions): any => this.cookieStorage.get(key, options);

  /**
   * Get all items from cookie like object map with key: pair.
   */
  getAll = (options?: CookieGetOptions): any => this.cookieStorage.getAll(options);

  /**
   * Removes item from cookie with accepted key. Removes from Cookies internal collection by deleting item, and setup
   * expires in document.cookie for cookie to new Date(1970, 1, 1, 0, 0, 1) and maxAge to 0.
   */
  remove = (key: string, options?: CookieSetOptions) => this.cookieStorage.remove(key, options);

  /**
   * removes all items from cookie. Removes from Cookies internal collection by deleting all items, and setup expires
   * in document.cookie for every cookie to new Date(1970, 1, 1, 0, 0, 1) and maxAge to 0.
   */
  removeAll = (options?: CookieSetOptions): void => {
    const cookies = this.getAll();
    Object.keys(cookies).forEach((key) => this.cookieStorage.remove(key, options));
  };

  /**
   * Check if cookie item with accepted key exist.
   */
  hasCookie = (key: string): boolean => {
    const cookies = this.getAll();
    const keys = Object.keys(cookies);

    return keys.includes(key);
  };

  /**
   * Use addChangeListener method for listening on cookie change, emitted only after set or remove method.
   * Don't listen on external changes of document.cookie, only for changes happened on this Cookie class instance.
   */
  addChangeListener = (callback: CookieChangeListener) => this.cookieStorage.addChangeListener(callback);

  /**
   * Use removeChangeListener method to remove previously added listener.
   */
  removeChangeListener = (callback: CookieChangeListener) => this.cookieStorage.removeChangeListener(callback);
}

export default new CookieStorageService();
